
export interface ApiConfig {
    backendBaseUrl: string;
    s3BucketUrl: string;
    socketBaseUrl: string;
    appLogo: string;
    appFavicon: string;
    appEnv?: string;
    aiFeatureEnabled?: string;
    countryEnv?: string;
}

// Subscriber pattern - callbacks to be notified when config is ready
type ConfigSubscriber = (config: ApiConfig) => void;

interface ApiConfigStore {
    config: ApiConfig | null;
    subscribers: ConfigSubscriber[];
}

/**
 * The config and its subscriber list live on a process-global slot rather than in
 * module scope.
 *
 * Why: consumers reach this module through many specifiers (`@erpsquad/common`,
 * `/utils`, `/utils/api-config`, plus every component chunk that inlines it), and
 * under Module Federation each app bundles its own copy. With module-scoped `let`
 * state, the Host's `initializeApiConfig()` wrote to one copy while every other
 * copy still read `null` — and the `onApiConfigReady` subscribers registered
 * against those copies (the `setBaseUrl` wiring in utils/api.ts and utils/common.ts)
 * never fired, leaving their API clients pointed at no base URL.
 *
 * A shared Module Federation entry can't solve it here the way it does for the
 * React Contexts: api-config's own consumers (utils/common.ts, utils/api.ts,
 * constants/*) are inside the graph that `src/runtime` bundles, so routing them
 * through '@erpsquad/common/runtime' makes the runtime chunk import itself and the
 * federated app hangs on a shared-module request that can never settle.
 *
 * Symbol.for() keys into the cross-realm registry, so every copy — however it was
 * bundled — resolves to the same store. Duplicate module instances become harmless
 * instead of something the build has to prevent.
 */
const STORE_KEY = Symbol.for('@erpsquad/common.apiConfig');

function getStore(): ApiConfigStore {
    const globalScope = globalThis as typeof globalThis & {
        [STORE_KEY]?: ApiConfigStore;
    };

    if (!globalScope[STORE_KEY]) {
        globalScope[STORE_KEY] = { config: null, subscribers: [] };
    }

    return globalScope[STORE_KEY];
}

/**
 * Register a callback to be called when API config is initialized.
 * If config is already initialized, the callback is called immediately.
 * This allows modules to register their setBaseUrl calls without
 * worrying about initialization order.
 */
export function onApiConfigReady(callback: ConfigSubscriber): void {
    const store = getStore();

    if (store.config) {
        // Config already initialized, call immediately
        callback(store.config);
    } else {
        // Store for later when config is initialized
        store.subscribers.push(callback);
    }
}

export function initializeApiConfig(config: ApiConfig): void {
    const store = getStore();

    if (store.config) {
        console.warn('API configuration is already initialized. Overwriting with new values.');
    }

    // Validate required fields
    if (!config.backendBaseUrl) {
        throw new Error('backendBaseUrl is required in API configuration');
    }

    store.config = { ...config };

    // Notify all subscribers. These were registered by every module copy across
    // the Host and its remotes, so one call wires up all of them.
    store.subscribers.forEach(callback => {
        try {
            callback(store.config!);
        } catch (error) {
            console.error('Error in API config subscriber:', error);
        }
    });
}

export function getApiConfig(): ApiConfig {
    const { config } = getStore();

    if (!config) {
        throw new Error(
            'API configuration not initialized. Please call initializeApiConfig() before using API features. ' +
            'See documentation: https://github.com/erpforce/common#api-configuration'
        );
    }

    return config;
}

/**
 * Get API config if available, otherwise return null.
 * Use this for optional access where you want to gracefully handle
 * uninitialized state.
 */
export function getApiConfigSafe(): ApiConfig | null {
    return getStore().config;
}

export function isApiConfigInitialized(): boolean {
    return getStore().config !== null;
}


export function resetApiConfig(): void {
    // Subscribers are intentionally kept: they are registered once at module load,
    // so clearing them would stop a later initializeApiConfig() from re-wiring the
    // base URLs. Matches the pre-existing behaviour.
    getStore().config = null;
}
